import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { CartLine, Order, ShippingAddress } from "./types";
import { getProductById } from "./data/products";

interface CartStore {
	lines: CartLine[];
	orders: Order[];
	addToCart: (productId: string, quantity?: number) => void;
	updateQuantity: (productId: string, quantity: number) => void;
	removeLine: (productId: string) => void;
	clearCart: () => void;
	placeOrder: (shipping: ShippingAddress) => Order;
	getOrder: (orderId: string) => Order | undefined;
	subtotal: () => number;
	itemCount: () => number;
}

export const useCartStore = create<CartStore>()(
	persist(
		(set, get) => ({
			lines: [],
			orders: [],
			addToCart: (productId, quantity = 1) => {
				if (quantity < 1) return;
				const existing = get().lines.find((line) => line.productId === productId);
				const updated = existing
					? get().lines.map((line) =>
							line.productId === productId ? { ...line, quantity: line.quantity + quantity } : line
						)
					: [...get().lines, { productId, quantity }];
				set({ lines: updated });
			},
			updateQuantity: (productId, quantity) => {
				if (quantity < 1) {
					set({ lines: get().lines.filter((line) => line.productId !== productId) });
					return;
				}
				set({
					lines: get().lines.map((line) =>
						line.productId === productId ? { ...line, quantity } : line
					),
				});
			},
			removeLine: (productId) => {
				set({ lines: get().lines.filter((line) => line.productId !== productId) });
			},
			clearCart: () => set({ lines: [] }),
			placeOrder: (shipping) => {
				const lines = get().lines;
				const order: Order = {
					id: `order-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`,
					createdAt: new Date().toISOString(),
					lines: lines
						.map((line) => {
							const product = getProductById(line.productId);
							return product
								? {
										productId: line.productId,
										quantity: line.quantity,
										name: product.name,
										price: product.price,
									}
								: null;
						})
						.filter((item): item is NonNullable<typeof item> => item !== null),
					shipping,
					subtotal: get().subtotal(),
				};
				set({ orders: [...get().orders, order], lines: [] });
				return order;
			},
			getOrder: (orderId) => get().orders.find((order) => order.id === orderId),
			subtotal: () =>
				get().lines.reduce((total, line) => {
					const product = getProductById(line.productId);
					return total + (product ? product.price * line.quantity : 0);
				}, 0),
			itemCount: () => get().lines.reduce((total, line) => total + line.quantity, 0),
		}),
		{ name: "xitester-sut-cart" }
	)
);
